/** @prettier */

import {useEffect, useState} from 'react';
import {Box, Container, Grid, Tooltip} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridCsvExportMenuItem,
} from '@mui/x-data-grid';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {utcToZonedTime, format} from 'date-fns-tz';
import {useLoaderData, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import {DateTime} from 'luxon';

//interface import
import * as apiIF from '../../Type/api';
//MTH component import
import {PrimaryButton} from '../../component/BasicButton';
//API import
import {
  getReportAnalysis,
  getCompositionsSitesBySiteID,
  getSettingUser,
} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';
import {checkSiteInfoBillingStatus} from '../../functions/checkBillingStatus';
import {decideLuxonDatePickerLocale} from '../../functions/decideLuxonDatePickerLocale';

/** 検索結果テーブル表示用の型 */
export type ConditionHistoryTableType = {
  id: number;
  /** 異常・不調・メンテナンスコードが発生した日付 */
  date: string;
  /** 機種分類 */
  unitType: string;
  /** ゲートウェイの名前 */
  gwName: string;
  /** フロア */
  floor: string;
  /** グループの名前 */
  groupName: string;
  /** 空調機の名前 */
  unitName: string;
  /** 発生した状態 */
  condition: string;
  /** SLアドレス */
  slAddress: string;
  /** RTアドレス */
  rtAddress: string;
  /** 室内機アドレス */
  iuAddress: string;
  /** 注意コードの翻訳用に変換前の不調値を保存するための変数 */
  originalCondition: string;
};

type loaderReturn = {
  /** サイトの名前 */
  siteName: apiIF.res.getCompositionsSitesBySiteID['site-name'];
  /** タイムゾーン */
  timezone: apiIF.res.getCompositionsSitesBySiteID['timezone'];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //全てのユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));

    const response = await Promise.all(promises);
    const siteInfo = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const unitExistence = response[1] as unitExistence;

    //全てのユニットが未登録ならリダイレクト
    const unregistered = Object.values(unitExistence).every(value => {
      return value === false;
    });
    if (unregistered) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    //全GWに課金情報がなければリダイレクト
    if (checkSiteInfoBillingStatus(siteInfo['billing-status']) === false) {
      throw new Error('Billing status does not exist');
    }

    return {siteName: siteInfo['site-name'], timezone: siteInfo.timezone};
  } catch {
    return undefined;
  }
}

/**
 * 異常不調履歴画面を表示するコンポーネント
 * @returns JSX.Element
 */
const ConditionHistory = () => {
  const trans = useTranslation().t; //翻訳関数
  /** サイトID */
  const {siteID} = useParams();
  /** サイトの情報 */
  const siteInfo = useLoaderData() as loaderReturn | undefined;

  /**
   * タイムゾーンが存在する場合、そのタイムゾーンに基づいた現在の日付と時刻
   * 存在しない場合、ブラウザ依存の現在時刻
   */
  const nowDate = siteInfo
    ? DateTime.now().setZone(siteInfo.timezone)
    : DateTime.now();
  /** 検索期間の開始日 */
  //prettier-ignore
  const [startDate, setStartDate] = useState<DateTime | null>(nowDate.minus({month:1}).startOf('day')); //初期値は1か月前
  /** 検索期間の終了日 */
  const [endDate, setEndDate] = useState<DateTime | null>(
    nowDate.startOf('day')
  ); //初期値は当日
  /** 検索結果テーブルの行データ */
  const [rows, setRows] = useState<ConditionHistoryTableType[]>([]);
  /** 検索中API待ち状態 */
  const [searchAPIwait, setSearchAPIwait] = useState(false);
  /** 検索データが見つからなかった */
  const [isNotFoundData, setIsNotFoundData] = useState(false);
  //prettier-ignore
  /** 言語情報 */
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english');

  /** 不調履歴詳細（labelは翻訳用のキー） */
  const badConditionDetail = [
    {
      id: 'W01',
      label: trans('ConditionHistory.content.table.badCondition.w01'),
    },
    {
      id: 'W01-01',
      label: trans('ConditionHistory.content.table.badCondition.w0101'),
    },
    {
      id: 'W01-02',
      label: trans('ConditionHistory.content.table.badCondition.w0102'),
    },
    {
      id: 'W01-03',
      label: trans('ConditionHistory.content.table.badCondition.w0103'),
    },
    {
      id: 'W01-04',
      label: trans('ConditionHistory.content.table.badCondition.w0104'),
    },
    {
      id: 'W01-05',
      label: trans('ConditionHistory.content.table.badCondition.w0105'),
    },
    {
      id: 'W01-06',
      label: trans('ConditionHistory.content.table.badCondition.w0106'),
    },
    {
      id: 'W01-07',
      label: trans('ConditionHistory.content.table.badCondition.w0107'),
    },
    {
      id: 'W01-08',
      label: trans('ConditionHistory.content.table.badCondition.w0108'),
    },
    {
      id: 'W01-09',
      label: trans('ConditionHistory.content.table.badCondition.w0109'),
    },
    {
      id: 'W01-10',
      label: trans('ConditionHistory.content.table.badCondition.w0110'),
    },
    {
      id: 'W01-11',
      label: trans('ConditionHistory.content.table.badCondition.w0111'),
    },
    {
      id: 'W01-12',
      label: trans('ConditionHistory.content.table.badCondition.w0112'),
    },
    {
      id: 'W01-13',
      label: trans('ConditionHistory.content.table.badCondition.w0113'),
    },
    {
      id: 'W01-14',
      label: trans('ConditionHistory.content.table.badCondition.w0114'),
    },
    {
      id: 'W01-15',
      label: trans('ConditionHistory.content.table.badCondition.w0115'),
    },
    {
      id: 'W01-16',
      label: trans('ConditionHistory.content.table.badCondition.w0116'),
    },
    {
      id: 'W01-17',
      label: trans('ConditionHistory.content.table.badCondition.w0117'),
    },
    {
      id: 'W01-18',
      label: trans('ConditionHistory.content.table.badCondition.w0118'),
    },
    {
      id: 'W01-19',
      label: trans('ConditionHistory.content.table.badCondition.w0119'),
    },
    {
      id: 'W01-20',
      label: trans('ConditionHistory.content.table.badCondition.w0120'),
    },
    {
      id: 'W01-21',
      label: trans('ConditionHistory.content.table.badCondition.w0121'),
    },
    {
      id: 'W01-22',
      label: trans('ConditionHistory.content.table.badCondition.w0122'),
    },
    {
      id: 'W01-24',
      label: trans('ConditionHistory.content.table.badCondition.w0124'),
    },
    {
      id: 'W01-26',
      label: trans('ConditionHistory.content.table.badCondition.w0126'),
    },

    {
      id: 'W01-28',
      label: trans('ConditionHistory.content.table.badCondition.w0128'),
    },
    {
      id: 'W01-30',
      label: trans('ConditionHistory.content.table.badCondition.w0130'),
    },
    {
      id: 'W01-31',
      label: trans('ConditionHistory.content.table.badCondition.w0131'),
    },
    {
      id: 'W01-32',
      label: trans('ConditionHistory.content.table.badCondition.w0132'),
    },
    {
      id: 'W01-40',
      label: trans('ConditionHistory.content.table.badCondition.w0140'),
    },
    {
      id: 'W01-41',
      label: trans('ConditionHistory.content.table.badCondition.w0141'),
    },
    {
      id: 'W01-42',
      label: trans('ConditionHistory.content.table.badCondition.w0142'),
    },
    {
      id: 'W01-43',
      label: trans('ConditionHistory.content.table.badCondition.w0143'),
    },
    {
      id: 'W01-44',
      label: trans('ConditionHistory.content.table.badCondition.w0144'),
    },
    {
      id: 'W01-45',
      label: trans('ConditionHistory.content.table.badCondition.w0145'),
    },
    {
      id: 'W01-46',
      label: trans('ConditionHistory.content.table.badCondition.w0146'),
    },
    {
      id: 'W01-47',
      label: trans('ConditionHistory.content.table.badCondition.w0147'),
    },
    {
      id: 'W01-48',
      label: trans('ConditionHistory.content.table.badCondition.w0148'),
    },
    {
      id: 'W01-49',
      label: trans('ConditionHistory.content.table.badCondition.w0149'),
    },
    {
      id: 'W01-50',
      label: trans('ConditionHistory.content.table.badCondition.w0150'),
    },
    {
      id: 'W01-55',
      label: trans('ConditionHistory.content.table.badCondition.w0155'),
    },
    {
      id: 'W01-57',
      label: trans('ConditionHistory.content.table.badCondition.w0157'),
    },
    {
      id: 'W01-59',
      label: trans('ConditionHistory.content.table.badCondition.w0159'),
    },
    {
      id: 'W01-61',
      label: trans('ConditionHistory.content.table.badCondition.w0161'),
    },
    {
      id: 'W01-62',
      label: trans('ConditionHistory.content.table.badCondition.w0162'),
    },
    {
      id: 'W01-65',
      label: trans('ConditionHistory.content.table.badCondition.w0165'),
    },
    {
      id: 'W01-66',
      label: trans('ConditionHistory.content.table.badCondition.w0166'),
    },
    {
      id: 'W01-67',
      label: trans('ConditionHistory.content.table.badCondition.w0167'),
    },
    {
      id: 'W01-69',
      label: trans('ConditionHistory.content.table.badCondition.w0169'),
    },
    {
      id: 'W01-71',
      label: trans('ConditionHistory.content.table.badCondition.w0171'),
    },
    {
      id: 'W01-73',
      label: trans('ConditionHistory.content.table.badCondition.w0173'),
    },
    {
      id: 'W01-75',
      label: trans('ConditionHistory.content.table.badCondition.w0175'),
    },
    {
      id: 'W01-77',
      label: trans('ConditionHistory.content.table.badCondition.w0177'),
    },
    {
      id: 'W01-78',
      label: trans('ConditionHistory.content.table.badCondition.w0178'),
    },
    {
      id: 'W01-80',
      label: trans('ConditionHistory.content.table.badCondition.w0180'),
    },
    {
      id: 'W01-81',
      label: trans('ConditionHistory.content.table.badCondition.w0181'),
    },
    {
      id: 'W01-82',
      label: trans('ConditionHistory.content.table.badCondition.w0182'),
    },
    {
      id: 'W01-83',
      label: trans('ConditionHistory.content.table.badCondition.w0183'),
    },
    {
      id: 'W01-84',
      label: trans('ConditionHistory.content.table.badCondition.w0184'),
    },
    {
      id: 'W01-90',
      label: trans('ConditionHistory.content.table.badCondition.w0190'),
    },
    {
      id: 'W01-91',
      label: trans('ConditionHistory.content.table.badCondition.w0191'),
    },
    {
      id: 'W01-92',
      label: trans('ConditionHistory.content.table.badCondition.w0192'),
    },
    {
      id: 'W02',
      label: trans('ConditionHistory.content.table.badCondition.w02'),
    },
    {
      id: 'W03',
      label: trans('ConditionHistory.content.table.badCondition.w03'),
    },
    {
      id: 'W03-01',
      label: trans('ConditionHistory.content.table.badCondition.w0301'),
    },
    {
      id: 'W03-02',
      label: trans('ConditionHistory.content.table.badCondition.w0302'),
    },
    {
      id: 'W03-03',
      label: trans('ConditionHistory.content.table.badCondition.w0303'),
    },
    {
      id: 'W03-04',
      label: trans('ConditionHistory.content.table.badCondition.w0304'),
    },
    {
      id: 'W03-05',
      label: trans('ConditionHistory.content.table.badCondition.w0305'),
    },
    {
      id: 'W03-06',
      label: trans('ConditionHistory.content.table.badCondition.w0306'),
    },
    {
      id: 'W03-07',
      label: trans('ConditionHistory.content.table.badCondition.w0307'),
    },
    {
      id: 'W04',
      label: trans('ConditionHistory.content.table.badCondition.w04'),
    },
    {
      id: 'W81',
      label: trans('ConditionHistory.content.table.badCondition.w81'),
    },
    {
      id: 'W82',
      label: trans('ConditionHistory.content.table.badCondition.w82'),
    },
  ];
  /* テーブル項目設定 */
  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: `${trans('ConditionHistory.content.table.headerName.date')}`,
      minWidth: 190,
    },
    {
      field: 'unitType',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.unitType'
      )}`,
      minWidth: 140,
    },
    {
      field: 'gwName',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.gatewayName'
      )}`,
      minWidth: 180,
    },
    {
      field: 'floor',
      headerName: `${trans('ConditionHistory.content.table.headerName.floor')}`,
      minWidth: 90,
    },
    {
      field: 'groupName',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.groupName'
      )}`,
      minWidth: 180,
    },
    {
      field: 'unitName',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.unitName'
      )}`,
      minWidth: 180,
    },
    {
      field: 'condition',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.condition'
      )}`,
      minWidth: 160,
      renderCell: (params: any) => {
        //不調履歴詳細をリストから探す
        let label = '';
        for (let data of badConditionDetail) {
          if (params.value === data['id']) {
            label = data['label'];
          }
        }

        return (
          //ツールチップで表示する
          <Tooltip title={label}>
            <span>{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'slAddress',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.supperLinkAddress'
      )}`,
      minWidth: 90,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Tooltip title="Supper Link Address">
          <span>
            {trans(
              'ConditionHistory.content.table.headerName.supperLinkAddress'
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'rtAddress',
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.remoteTerminalAddress'
      )}`,
      minWidth: 90,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Tooltip title="Remote Terminal Address">
          <span>
            {trans(
              'ConditionHistory.content.table.headerName.remoteTerminalAddress'
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'iuAddress',
      minWidth: 90,
      headerName: `${trans(
        'ConditionHistory.content.table.headerName.inUnitAddress'
      )}`,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Tooltip title="Indoor Unit Address">
          <span>
            {trans('ConditionHistory.content.table.headerName.inUnitAddress')}
          </span>
        </Tooltip>
      ),
    },
  ];
  /** 注意コードの変換テーブル*/
  const conditionsMapping: {[key: string]: string} = {
    C01: trans('ConditionHistory.content.table.cCodeName.C01'),
  };

  /** カレンダで選択可能な日付（下限） */
  const minDate = nowDate.minus({year: 10}).startOf('day');
  /** カレンダで選択可能な日付（上限） */
  const maxDate = nowDate.startOf('day');

  //言語設定変更時に表示データを変更する
  useEffect(() => {
    //言語情報を取得する
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
    /**
     * 言語設定に基づいて行データの条件を変換する
     * @returns  変換された行データの配列
     */
    const getRowsForLanguage = () => {
      return rows.map(row => {
        return {
          ...row,
          condition: conditionsMapping[row.originalCondition] || row.condition,
        };
      });
    };
    setRows(getRowsForLanguage());
  }, [trans]);

  /**
   * フロア名変換テーブル
   * @param floor フロア
   * @returns フロア名
   */
  const convertFloorName = (floor: string) => {
    switch (floor) {
      case '14':
        return trans('ConditionHistory.content.table.floor.floor14');
      case '13':
        return trans('ConditionHistory.content.table.floor.floor13');
      case '12':
        return trans('ConditionHistory.content.table.floor.floor12');
      case '11':
        return trans('ConditionHistory.content.table.floor.floor11');
      case '10':
        return trans('ConditionHistory.content.table.floor.floor10');
      case '9':
        return trans('ConditionHistory.content.table.floor.floor9');
      case '8':
        return trans('ConditionHistory.content.table.floor.floor8');
      case '7':
        return trans('ConditionHistory.content.table.floor.floor7');
      case '6':
        return trans('ConditionHistory.content.table.floor.floor6');
      case '5':
        return trans('ConditionHistory.content.table.floor.floor5');
      case '4':
        return trans('ConditionHistory.content.table.floor.floor4');
      case '3':
        return trans('ConditionHistory.content.table.floor.floor3');
      case '2':
        return trans('ConditionHistory.content.table.floor.floor2');
      case '1':
        return trans('ConditionHistory.content.table.floor.floor1');
      case '0':
        return trans('ConditionHistory.content.table.floor.floor0');
      case '-1':
        return trans('ConditionHistory.content.table.floor.basement1');
      case '-2':
        return trans('ConditionHistory.content.table.floor.basement2');
      case '-3':
        return trans('ConditionHistory.content.table.floor.basement3');
      case '-4':
        return trans('ConditionHistory.content.table.floor.basement4');
      default:
        return '';
    }
  };

  /**
   * Searchボタン押下時に異常不調履歴情報を取得する
   */
  const onClickSearchButton = () => {
    if (siteID === undefined || siteInfo === undefined) return;

    //API応答待ち状態
    setSearchAPIwait(true);

    //開始日または終了日が空欄の場合
    if (startDate === null || endDate === null) {
      alert(trans('ConditionHistory.alertMsg.inputErr.searchPeriod'));
      setSearchAPIwait(false);
      return;
    }

    //検索期間の終了日が開始日より前にある場合
    if (endDate < startDate) {
      alert(trans('ConditionHistory.alertMsg.inputErr.reversePeriod'));
      setSearchAPIwait(false);
      return;
    }

    //検索期間が6ヶ月以上の場合
    //開始日から6カ月後の同日（存在しない場合は月末に補正）
    const sixMonthsLater = startDate.plus({month: 6});
    if (endDate > sixMonthsLater) {
      alert(`*${trans('ConditionHistory.alertMsg.inputErr.range')}`);
      setSearchAPIwait(false);
      return;
    }

    //検索可能範囲より前を指定されていた場合
    if (startDate < minDate) {
      alert(trans('ConditionHistory.alertMsg.inputErr.maxBefore'));
      setSearchAPIwait(false);
      return;
    }

    /** 開始日 */
    const afterTime = startDate.startOf('day').toUTC().toISO(); //選択日の00:00:00
    /** 終了日 */
    const beforeTime = endDate.plus({day: 1}).startOf('day').toUTC().toISO(); //選択日+1日の00:00:00

    // 開始日・終了日がエラーの場合
    if (afterTime === null || beforeTime === null) {
      alert(trans('ConditionHistory.alertMsg.inputErr.searchPeriod'));
      setSearchAPIwait(false);
      return;
    }

    //検索データが見つからないフラグ初期化
    setIsNotFoundData(false);

    getReportAnalysis(siteID, {
      'after-time': afterTime,
      'before-time': beforeTime,
      'max-result': 100,
    })
      .then(response => {
        if (response['conditions'].length === 0) {
          setIsNotFoundData(true);
          setSearchAPIwait(false);
          setRows([]);
          return;
        }

        /** テーブル表示用データ */
        const conditionsData = response['conditions'].map((conditions, num) => {
          let result: ConditionHistoryTableType = {
            id: num,
            date: '-',
            unitType: '-',
            gwName: '-',
            floor: '-',
            groupName: '-',
            unitName: '-',
            condition: '-',
            slAddress: '-',
            rtAddress: '-',
            iuAddress: '-',
            originalCondition: '-',
          };

          //異常・不調・メンテナンスコードが発生した日付
          if (conditions['date'] !== '') {
            result.date = format(
              utcToZonedTime(conditions['date'], siteInfo.timezone),
              'dd/MM/yyyy HH:mm:ss'
            );
          }
          //機種分類
          //SLアドレス
          //RTアドレス
          //室内機アドレス
          switch (conditions['unit-type']) {
            case 'airConditioner':
              result.unitType = trans('ConditionHistory.unitName.ac');
              if (conditions['outdoor-unit-address'] !== '') {
                result.slAddress = conditions['outdoor-unit-address'];
              }
              if (conditions['indoor-unit-address'] !== '') {
                result.iuAddress = conditions['indoor-unit-address'];
              }
              break;
            case 'qtonOpen':
            case 'qtonClose':
              result.unitType = trans('ConditionHistory.unitName.q-ton');
              if (conditions['outdoor-unit-address'] !== '') {
                result.rtAddress = conditions['outdoor-unit-address'];
              }
              break;
            case 'hyozan':
              result.unitType = trans('ConditionHistory.unitName.hyozan');
              if (conditions['outdoor-unit-address'] !== '') {
                result.rtAddress = conditions['outdoor-unit-address'];
              }
              if (conditions['indoor-unit-address'] !== '') {
                result.iuAddress = conditions['indoor-unit-address'];
              }
              break;
            case 'chiller':
              result.unitType = trans('ConditionHistory.unitName.hydrolution');
              if (conditions['outdoor-unit-address'] !== '') {
                result.rtAddress = conditions['outdoor-unit-address'];
              }
              break;
            case 'hccvq':
              result.unitType = trans('ConditionHistory.unitName.hccvq');
              if (conditions['outdoor-unit-address'] !== '') {
                result.rtAddress = conditions['outdoor-unit-address'];
              }
              if (conditions['indoor-unit-address'] !== '') {
                result.iuAddress = conditions['indoor-unit-address'];
              }
              break;
            default:
          }
          //ゲートウェイの名前
          if (conditions['gateway-name'] !== '') {
            result.gwName = conditions['gateway-name'];
          }
          //フロア
          if (conditions['floor'] !== '') {
            result.floor = convertFloorName(conditions['floor']);
          }
          //グループの名前
          if (conditions['group-name'] !== '') {
            result.groupName = conditions['group-name'];
          }
          //空調機の名前
          if (conditions['unit-name'] !== '') {
            result.unitName = conditions['unit-name'];
          }
          //発生した状態
          if (conditions['condition'] !== '') {
            const conditionCode = conditions['condition'];
            result.condition =
              conditionsMapping[conditionCode] || conditionCode;
            result.originalCondition = conditions['condition'];
          }

          return result;
        });
        setRows(conditionsData);
      })
      .catch(err => {
        alert(err['message']);
      })
      .finally(() => {
        setSearchAPIwait(false);
      });
  };

  /**
   * カスタムツールバー
   * @returns JSX.Element
   */
  const CustomToolbar = () => {
    /** 出力用ファイル名 */
    let fileName = 'ErrorHistory'; //必要なデータが取得できない場合のファイル名

    if (siteInfo !== undefined && startDate !== null && endDate !== null) {
      //prettier-ignore
      fileName = `${siteInfo.siteName}_${startDate.toFormat('yyyyMMdd')}_${endDate.toFormat('yyyyMMdd')}`;
    }

    return (
      <GridToolbarContainer>
        {/* カラムの表示/非表示ボタン */}
        <GridToolbarColumnsButton />
        {/* フィルター機能ボタン */}
        <GridToolbarFilterButton />
        {/* 行の密度選択ボタン */}
        <GridToolbarDensitySelector />
        {/* データのエクスポートボタン */}
        <GridToolbarExportContainer>
          <GridCsvExportMenuItem options={{fileName: fileName}} />
        </GridToolbarExportContainer>
      </GridToolbarContainer>
    );
  };

  return (
    <Container fixed>
      <Grid container rowSpacing={2}>
        {/* タイトル */}
        <Grid item xs={12}>
          <h1>{trans('ConditionHistory.title')}</h1>
          {/* 説明文 */}
          <h2>{trans('ConditionHistory.explanation')}</h2>
        </Grid>
        {/* 検索欄 */}
        <Grid
          item
          xs={12}
          container
          justifyContent="space-around"
          alignItems="center"
          rowSpacing={2}>
          {/* 期間 */}
          <Grid
            item
            xs={12}
            lg
            className="serviceInfo__operationConditionHistory__period">
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs="auto">
                {trans('ConditionHistory.content.period.title')}
                &nbsp;:&nbsp;
              </Grid>
              <Grid item xs="auto">
                <LocalizationProvider
                  dateAdapter={AdapterLuxon}
                  adapterLocale={decideLuxonDatePickerLocale(language)}>
                  <DatePicker
                    autoFocus
                    format="dd/MM/yyyy"
                    label={trans('ConditionHistory.content.startDate')}
                    maxDate={maxDate}
                    minDate={minDate}
                    onChange={date =>
                      setStartDate(date ? date.startOf('day') : date)
                    }
                    value={startDate}
                    views={['year', 'month', 'day']}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs="auto">
                {trans('ConditionHistory.content.period.to')}
              </Grid>
              <Grid item xs>
                <LocalizationProvider
                  dateAdapter={AdapterLuxon}
                  adapterLocale={decideLuxonDatePickerLocale(language)}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    label={trans('ConditionHistory.content.endDate')}
                    maxDate={maxDate}
                    minDate={minDate}
                    onChange={date => setEndDate(date)}
                    value={endDate}
                    views={['year', 'month', 'day']}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid
                item
                xs={12}
                className="serviceInfo__operationConditionHistory__period__helper">
                {`*${trans('ConditionHistory.content.period.helperText')}`}
              </Grid>
            </Grid>
          </Grid>
          {/* 検索ボタン */}
          <Grid
            item
            xs={12}
            lg="auto"
            className="serviceInfo__operationConditionHistory__request">
            <PrimaryButton
              onClick={onClickSearchButton}
              id="mv-conditionsSearchBtn"
              disabled={searchAPIwait}
              className="serviceInfo__operationConditionHistory__request__button"
              buttonText={trans('ConditionHistory.content.period.buttonTxt')}
            />
          </Grid>
        </Grid>
        {/* 検索結果テーブル */}
        <Grid
          item
          xs={12}
          className="serviceInfo__operationConditionHistory__result--search">
          <DataGrid
            columns={columns}
            rows={rows}
            slots={{
              toolbar: CustomToolbar, // カスタムツールバー
              noRowsOverlay: () => (
                <Box className="serviceInfo__operationConditionHistory__result--search__message">
                  {isNotFoundData === false
                    ? //検索前の場合
                      trans(
                        'ConditionHistory.content.table.message.noRowsLabel'
                      )
                    : //検索してデータが見つからなかった場合
                      trans(
                        'ConditionHistory.content.table.message.notFoundSearchData'
                      )}
                </Box>
              ),
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  slAddress: false,
                  rtAddress: false,
                  iuAddress: false,
                },
              },
              pagination: {paginationModel: {pageSize: 25}},
            }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            hideFooterSelectedRowCount
            density="standard"
            loading={searchAPIwait}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ConditionHistory;
